import React, { useEffect, useRef, useState } from 'react';

import { PanelSize } from '../components/pingu/types';

// Keeps the panel clear of the viewport edge it is dragged towards.
const EDGE_MARGIN = 12;

export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export const RESIZE_CURSORS: Record<ResizeEdge, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

const useResizablePanel = (initialSize: PanelSize, minSize: PanelSize) => {
  const [size, setSize] = useState(initialSize);
  const [position, setPosition] = useState({ right: 16, bottom: 16 });
  const stopResizeRef = useRef<(() => void) | null>(null);
  const stopMoveRef = useRef<(() => void) | null>(null);

  // A drag still live when the panel unmounts would leave its listeners behind.
  useEffect(() => () => {
    stopResizeRef.current?.();
    stopMoveRef.current?.();
  }, []);

  const startMove = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startPosition = position;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const maxRight = Math.max(12, window.innerWidth - size.width - 12);
      const maxBottom = Math.max(12, window.innerHeight - size.height - 12);
      const nextRight = Math.min(
        Math.max(startPosition.right - (moveEvent.clientX - startX), 12),
        maxRight,
      );
      const nextBottom = Math.min(
        Math.max(startPosition.bottom - (moveEvent.clientY - startY), 12),
        maxBottom,
      );
      setPosition({ right: nextRight, bottom: nextBottom });
    };

    const stopMove = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopMove);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      stopMoveRef.current = null;
    };

    stopMoveRef.current = stopMove;
    document.body.style.cursor = 'move';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopMove);
  };

  const startResize = (event: React.MouseEvent<HTMLElement>, edge: ResizeEdge) => {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startSize = size;
    const startPosition = position;
    const resizesWest = edge.includes('w');
    const resizesEast = edge.includes('e');
    const resizesNorth = edge.includes('n');
    const resizesSouth = edge.includes('s');

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      let { width, height } = startSize;
      let { right, bottom } = startPosition;

      if (resizesWest) {
        const maxWidth = window.innerWidth - startPosition.right - EDGE_MARGIN;
        width = Math.min(Math.max(startSize.width - deltaX, minSize.width), maxWidth);
      }
      if (resizesEast) {
        const maxWidth = startSize.width + startPosition.right - EDGE_MARGIN;
        width = Math.min(Math.max(startSize.width + deltaX, minSize.width), maxWidth);
        right = startPosition.right - (width - startSize.width);
      }
      if (resizesNorth) {
        const maxHeight = window.innerHeight - startPosition.bottom - EDGE_MARGIN;
        height = Math.min(Math.max(startSize.height - deltaY, minSize.height), maxHeight);
      }
      if (resizesSouth) {
        const maxHeight = startSize.height + startPosition.bottom - EDGE_MARGIN;
        height = Math.min(Math.max(startSize.height + deltaY, minSize.height), maxHeight);
        bottom = startPosition.bottom - (height - startSize.height);
      }

      setSize({ width, height });
      setPosition({ right, bottom });
    };

    const stopResize = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResize);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      stopResizeRef.current = null;
    };

    stopResizeRef.current = stopResize;
    document.body.style.cursor = RESIZE_CURSORS[edge];
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  return {
    size, position, startMove, startResize,
  };
};
export default useResizablePanel;
